/**
 * What a boat-to-boat hit costs the boat at fault, on the same ladder as the shore: a nudge is
 * a racing incident, a medium hit is a scrape, a hard hit is a crash. The boat that was hit only
 * takes the shove the impulse already gave it.
 */
import { applyCrashCost, driveLevel, SCRAPE_SPEED_KEEP, scrapeDrive } from '../../crashCost.ts';
import type { BoatHitSeverity } from '../host.ts';
import type { BoatState } from '../../types.ts';
import { BOAT_HARD_CLOSING_PX, BOAT_NUDGE_CLOSING_PX } from './constants.ts';

export function classifyBoatHit(closingSpeed: number): BoatHitSeverity {
  if (closingSpeed < BOAT_NUDGE_CLOSING_PX) {
    return 'nudge';
  }

  return closingSpeed < BOAT_HARD_CLOSING_PX ? 'medium' : 'hard';
}

const RANK: Readonly<Record<BoatHitSeverity, number>> = { nudge: 0, medium: 1, hard: 2 };

export function worseSeverity(a: BoatHitSeverity | null, b: BoatHitSeverity): BoatHitSeverity {
  return a !== null && RANK[a] >= RANK[b] ? a : b;
}

/**
 * The landing of a boat that caused a hit of `severity` this turn. `start` is the boat before
 * its move, as a scrape steps down from there. Anything the shore already took during the drift
 * stays: the result is never faster or on a bigger circle than `landed`. Harshness 0 forgives
 * boats as it forgives the shore.
 */
export function penalizeAtFault(
  start: BoatState,
  landed: BoatState,
  severity: BoatHitSeverity,
  harshness: number,
): BoatState {
  if (severity === 'nudge' || harshness <= 0) {
    return landed;
  }

  if (severity === 'medium') {
    return {
      ...landed,
      speed: Math.max(0, landed.speed * SCRAPE_SPEED_KEEP),
      drive: Math.min(driveLevel(landed), scrapeDrive(start)) as 0 | 1 | 2,
    };
  }

  return { ...landed, speed: applyCrashCost(landed.speed), drive: 0 };
}
